import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import type { SentimentBreakdown } from "@type/aiqm";

interface SentimentDistributionDonutProps {
    breakdown: SentimentBreakdown;
    netSentiment: number;
}

const COLORS = {
    positive: "#10b981", // emerald-500
    neutral: "#d1d5db", // gray-300
    negative: "#f43f5e", // rose-500
};

function netLabel(score: number): string {
    return `${score > 0 ? "+" : ""}${score}`;
}

function normalizedPercentages(
    breakdown: SentimentBreakdown
): Record<keyof SentimentBreakdown, number> {
    const entries = ([
        ["positive", breakdown.positive],
        ["neutral", breakdown.neutral],
        ["negative", breakdown.negative],
    ] as const).map(([key, value]) => ({
        key,
        value: Math.max(0, value),
    }));
    const total = entries.reduce((sum, entry) => sum + entry.value, 0);

    if (total === 0) {
        return { positive: 0, neutral: 0, negative: 0 };
    }

    const scaled = entries.map((entry) => {
        const exact = (entry.value / total) * 100;
        const floor = Math.floor(exact);
        return { ...entry, exact, floor, remainder: exact - floor };
    });
    let remainder = 100 - scaled.reduce((sum, entry) => sum + entry.floor, 0);

    scaled
        .sort((a, b) => b.remainder - a.remainder)
        .forEach((entry) => {
            if (remainder <= 0) return;
            entry.floor += 1;
            remainder -= 1;
        });

    return scaled.reduce(
        (acc, entry) => {
            acc[entry.key] = entry.floor;
            return acc;
        },
        { positive: 0, neutral: 0, negative: 0 } as Record<keyof SentimentBreakdown, number>
    );
}

export default function SentimentDistributionDonut({
    breakdown,
    netSentiment,
}: SentimentDistributionDonutProps) {
    const displayBreakdown = normalizedPercentages(breakdown);
    const data = [
        { key: "positive", label: "Positive", value: displayBreakdown.positive },
        { key: "neutral", label: "Neutral", value: displayBreakdown.neutral },
        { key: "negative", label: "Negative", value: displayBreakdown.negative },
    ];
    const allZero = data.every((d) => d.value === 0);

    return (
        <div className="flex items-center justify-center gap-6">
            {/* Donut */}
            <div className="relative h-[260px] w-[260px] flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={allZero ? [{ key: "empty", label: "", value: 1 }] : data}
                            dataKey="value"
                            nameKey="label"
                            cx="50%"
                            cy="50%"
                            innerRadius={86}
                            outerRadius={122}
                            paddingAngle={allZero ? 0 : 2}
                            stroke="none"
                            startAngle={90}
                            endAngle={-270}
                            isAnimationActive={false}
                        >
                            {(allZero ? [{ key: "empty" }] : data).map((d) => (
                                <Cell
                                    key={d.key}
                                    fill={
                                        allZero
                                            ? "#f1f5f9"
                                            : COLORS[d.key as keyof typeof COLORS]
                                    }
                                />
                            ))}
                        </Pie>
                    </PieChart>
                </ResponsiveContainer>
                {/* Center label: the net score */}
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-semibold tabular-nums text-gray-900">
                        {netLabel(netSentiment)}
                    </span>
                    <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                        net score
                    </span>
                </div>
            </div>

            {/* Legends — right side */}
            <div className="flex flex-col gap-3">
                {data.map((d) => (
                    <div key={d.key} className="flex items-center gap-2.5">
                        <span
                            className="h-3 w-3 flex-shrink-0 rounded-full"
                            style={{ backgroundColor: COLORS[d.key as keyof typeof COLORS] }}
                        />
                        <div className="flex flex-col">
                            <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                                {d.label}
                            </span>
                            <span className="text-base font-semibold tabular-nums text-gray-900">
                                {d.value}%
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
